import React from 'react';
import { Handle, Position } from 'reactflow';
import { Box, Paper, Typography, Chip, CircularProgress } from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Schedule } from '@mui/icons-material';

const BaseNode = ({ 
  data = {}, 
  id,
  selected,
  isConnectable, 
  icon, 
  color = '#9C27B0',
  showHandles = false, // Changed default since we handle connections in individual nodes
  title,
  type,
  statusIcon,
  primaryColor,
  secondaryColor,
  children 
}) => {
  const nodeColor = primaryColor || color;
  
  // Determine execution status from data
  const executionStatus = data?.executionStatus || data?.status;
  const hasExecuted = data?.output !== undefined || executionStatus !== undefined;
  
  // Get status indicator
  const getStatusIndicator = () => {
    if (executionStatus === 'running' || executionStatus === 'executing') {
      return (
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={16} sx={{ color: '#1976d2' }} />
          <Box
            sx={{
              position: 'absolute',
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: '2px solid transparent',
              borderTopColor: '#1976d2',
              borderRightColor: '#1976d2',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />
        </Box>
      );
    }
    if (executionStatus === 'success' || executionStatus === 'completed') {
      return <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />;
    }
    if (executionStatus === 'error' || executionStatus === 'failed') {
      return <ErrorIcon sx={{ fontSize: 16, color: '#f44336' }} />;
    }
    if (executionStatus === 'pending' || executionStatus === 'waiting') {
      return <Schedule sx={{ fontSize: 16, color: '#ff9800' }} />;
    }
    if (hasExecuted && data?.output) {
      // If there's output but no explicit status, assume success
      return <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />;
    }
    return null;
  };
  return (
    <Paper
      elevation={3}
      sx={{
        padding: 2,
        borderRadius: 2,
        border: '2px solid',
        borderColor: selected ? nodeColor : (executionStatus === 'running' ? '#1976d2' : 'transparent'),
        minWidth: 200,
        backgroundColor: 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
        overflow: 'visible',
        ...(executionStatus === 'running' && {
          boxShadow: '0 0 20px rgba(33, 150, 243, 0.4)',
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': {
              boxShadow: '0 0 20px rgba(33, 150, 243, 0.4)'
            },
            '50%': {
              boxShadow: '0 0 30px rgba(33, 150, 243, 0.6), 0 0 50px rgba(33, 150, 243, 0.3)'
            },
            '100%': {
              boxShadow: '0 0 20px rgba(33, 150, 243, 0.4)'
            }
          }
        }),
        '&:hover': {
          boxShadow: 6,
          borderColor: nodeColor,
        }
      }}
    >
      {showHandles && (
        <Handle
          type="target"
          position={Position.Left}
          style={{
            background: nodeColor,
            width: 12,
            height: 12,
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
          isConnectable={isConnectable}
        />
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ color: nodeColor, display: 'flex', mt: 0.5 }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {title || data?.label || data?.config?.label || 'Untitled'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {statusIcon || getStatusIndicator()}
              <Chip 
                label={id} 
                size="small" 
                variant="outlined"
                sx={{ 
                  height: 20, 
                  fontSize: '0.7rem',
                  opacity: 0.7,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </Box>
          </Box>
          {data?.config?.description && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
              {data.config.description}
            </Typography>
          )}
          {children}
        </Box>
      </Box>

      {showHandles && (
        <Handle
          type="source"
          position={Position.Right}
          style={{
            background: nodeColor,
            width: 12,
            height: 12,
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
          isConnectable={isConnectable}
        />
      )}
    </Paper>
  );
};

export default BaseNode;